import { ASPECT_RATIOS, DesignChannel } from 'components/NewFileModal/Types'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Archetype } from 'types'

interface NativeLayoutSpec {
  // Whether the native layout is enabled for the current user
  enabled: boolean
  // The archetypes to use for each channel. These will be used in order in the template field of the wizard.
  archetypes: Array<Archetype>
  // The default width for the archetype
  width: number
  // The default height for the archetype
  height: number
}

const LOGO_ARCHETYPES: Array<Archetype> = [Archetype.INSTAGRAM_POST_PHOTO_TEXT_LOGO, Archetype.HEADER_LOGO]

const isNotLogoArchetype = (archetype: Archetype) => !LOGO_ARCHETYPES.includes(archetype)

export const useNativeLayouts = (brandKitId?: number) => {
  const brandKitQuery = useBrandKit(brandKitId)
  const brandKit = brandKitQuery.data?.brand_kit
  const brandKitPrimaryLogos = brandKit?.brand_visual_kit?.logos.filter((logo) => logo.is_primary_logo) || []
  const hasLogos = brandKitPrimaryLogos.length > 0

  const mappings: Record<DesignChannel, NativeLayoutSpec> = {
    [DesignChannel.InstagramPost]: {
      enabled: useFlipperFlag('copilot_native_layouts_instagram_posts'),
      archetypes: [
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_PARAGRAPH_PHOTO,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_LOGO,
        Archetype.HEADER_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.HEADER_PHOTO,
        Archetype.PHOTO_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH_BOXED,
      ].filter((archetype) => (hasLogos ? true : isNotLogoArchetype(archetype))),
      width: ASPECT_RATIOS['instagram-portrait-post'].width,
      height: ASPECT_RATIOS['instagram-portrait-post'].height,
    },
    [DesignChannel.InstagramStory]: {
      enabled: useFlipperFlag('copilot_native_layouts_instagram_stories'),
      archetypes: [Archetype.INSTAGRAM_STORY_PHOTO_TEXT],
      width: ASPECT_RATIOS['instagram-story'].width,
      height: ASPECT_RATIOS['instagram-story'].height,
    },
    [DesignChannel.InstagramReel]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['instagram-reel'].width,
      height: ASPECT_RATIOS['instagram-reel'].height,
    },
    [DesignChannel.TikTokPost]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['tik-tok-post'].width,
      height: ASPECT_RATIOS['tik-tok-post'].height,
    },
    [DesignChannel.TikTokThumbnail]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['tik-tok-thumbnail'].width,
      height: ASPECT_RATIOS['tik-tok-thumbnail'].height,
    },
    [DesignChannel.YoutubeShort]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['youtube-short'].width,
      height: ASPECT_RATIOS['youtube-short'].height,
    },
    [DesignChannel.YoutubeProfilePicture]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['youtube-profile-picture'].width,
      height: ASPECT_RATIOS['youtube-profile-picture'].height,
    },
    [DesignChannel.YoutubeChannelArt]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['youtube-channel-art'].width,
      height: ASPECT_RATIOS['youtube-channel-art'].height,
    },
    [DesignChannel.FacebookCover]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['facebook-cover'].width,
      height: ASPECT_RATIOS['facebook-cover'].height,
    },
    [DesignChannel.FacebookProfilePicture]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['facebook-profile-picture'].width,
      height: ASPECT_RATIOS['facebook-profile-picture'].height,
    },
    [DesignChannel.FacebookPost]: {
      enabled: useFlipperFlag('copilot_native_layouts_instagram_posts'),
      archetypes: [
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_PARAGRAPH_PHOTO,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_LOGO,
        Archetype.HEADER_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.HEADER_PHOTO,
        Archetype.PHOTO_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH_BOXED,
      ].filter((archetype) => (hasLogos ? true : isNotLogoArchetype(archetype))),
      width: ASPECT_RATIOS['facebook-post'].width,
      height: ASPECT_RATIOS['facebook-post'].height,
    },
    [DesignChannel.FacebookMarketplace]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['facebook-marketplace'].width,
      height: ASPECT_RATIOS['facebook-marketplace'].height,
    },
    [DesignChannel.LinkedInBanner]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['linkedin-banner'].width,
      height: ASPECT_RATIOS['linkedin-banner'].height,
    },
    [DesignChannel.LinkedInProfilePicture]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['linkedin-profile-picture'].width,
      height: ASPECT_RATIOS['linkedin-profile-picture'].height,
    },
    [DesignChannel.LinkedInPost]: {
      enabled: useFlipperFlag('copilot_native_layouts_linkedin_x_posts'),
      archetypes: [
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_PARAGRAPH_PHOTO,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_LOGO,
        Archetype.HEADER_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.HEADER_PHOTO,
        Archetype.PHOTO_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH_BOXED,
      ].filter((archetype) => (hasLogos ? true : isNotLogoArchetype(archetype))),
      width: ASPECT_RATIOS['linkedin-post'].width,
      height: ASPECT_RATIOS['linkedin-post'].height,
    },
    [DesignChannel.TwitterProfilePicture]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['twitter-profile-picture'].width,
      height: ASPECT_RATIOS['twitter-profile-picture'].height,
    },
    [DesignChannel.TwitterCoverImage]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['twitter-cover-image'].width,
      height: ASPECT_RATIOS['twitter-cover-image'].height,
    },
    [DesignChannel.WhatsAppSticker]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['whatsapp-sticker'].width,
      height: ASPECT_RATIOS['whatsapp-sticker'].height,
    },
    [DesignChannel.Pinterest]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['pinterest'].width,
      height: ASPECT_RATIOS['pinterest'].height,
    },
    [DesignChannel.Ebay]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['ebay'].width,
      height: ASPECT_RATIOS['ebay'].height,
    },
    [DesignChannel.Poshmark]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['poshmark'].width,
      height: ASPECT_RATIOS['poshmark'].height,
    },
    [DesignChannel.Depop]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['depop'].width,
      height: ASPECT_RATIOS['depop'].height,
    },
    [DesignChannel.MercadoLibre]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['mercadolibre'].width,
      height: ASPECT_RATIOS['mercadolibre'].height,
    },
    [DesignChannel.Lazada]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['lazada'].width,
      height: ASPECT_RATIOS['lazada'].height,
    },
    [DesignChannel.Mercari]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['mercari'].width,
      height: ASPECT_RATIOS['mercari'].height,
    },
    [DesignChannel.Etsy]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['etsy'].width,
      height: ASPECT_RATIOS['etsy'].height,
    },
    [DesignChannel.Vinted]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['vinted'].width,
      height: ASPECT_RATIOS['vinted'].height,
    },
    [DesignChannel.Shopee]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['shopee'].width,
      height: ASPECT_RATIOS['shopee'].height,
    },
    [DesignChannel.Amazon]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['amazon'].width,
      height: ASPECT_RATIOS['amazon'].height,
    },
    [DesignChannel.Shopify]: {
      enabled: false,
      archetypes: [],
      width: ASPECT_RATIOS['shopify-square'].width,
      height: ASPECT_RATIOS['shopify-square'].height,
    },
    [DesignChannel.TwitterPost]: {
      enabled: useFlipperFlag('copilot_native_layouts_linkedin_x_posts'),
      archetypes: [
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_PARAGRAPH_PHOTO,
        Archetype.INSTAGRAM_POST_PHOTO_TEXT,
        Archetype.HEADER_LOGO,
        Archetype.HEADER_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.HEADER_PHOTO,
        Archetype.PHOTO_PARAGRAPH,
        Archetype.PHOTO,
        Archetype.PHOTO_HEADER_PARAGRAPH_BOXED,
      ].filter((archetype) => (hasLogos ? true : isNotLogoArchetype(archetype))),
      width: ASPECT_RATIOS['twitter-post'].width,
      height: ASPECT_RATIOS['twitter-post'].height,
    },
    [DesignChannel.Post]: { enabled: false, archetypes: [], width: 1080, height: 1350 },
  }

  return mappings
}
